import React from "react";
import { AbsoluteFill } from "remotion";
import { Sfx } from "../../b2b/ui/Sfx";
import { useMerchant } from "../copy";
import type { SceneProps } from "../timing";
import { shot } from "../merchants";
import { useStage } from "../stage";
import { Bubble, LinkPreview } from "../ui/Chat";
import { Backdrop, cardAspect, PhotoCard } from "../ui/Photo";

/**
 * Local frames: the friend asks just after the photo lands, you answer
 * ~0.5s later, so the reply (the brand, the answer the hook sets up) gets
 * ~2s on screen before the cut.
 */
const ASK = 8;
const REPLY = 24;

/**
 * Hook — the brand's best photo, then the scene every recommendation
 * starts from: a friend asks "what is it??", you answer with the brand, as
 * a link. The brand arrives as the answer to someone's curiosity (the
 * iMessage-reveal pattern), not as a logo slate. Frame 0 is already the
 * photo: no fade-in, no black first frame.
 */
export const Hook: React.FC<SceneProps> = () => {
  const { m, t, copy } = useMerchant();
  const stage = useStage();
  const img = shot(m, 0);

  // Vertical shapes: card on top, chat overlapping its bottom edge.
  // Wide: card left, chat in the right column.
  const wide = stage.shape === "wide";
  const cardW = { tall: 920, feed: 860, square: 720, wide: 960 }[stage.shape];
  const cardH = cardW / cardAspect(img);
  const font = { tall: 46, feed: 42, square: 36, wide: 44 }[stage.shape];
  // Vertical shapes: centre card + chat (≈ 6.5 lines of bubbles below the
  // card's edge) in the text-safe band.
  const band = [stage.safe.top, stage.h - stage.safe.bottom];
  const group = cardH + font * 6.5;
  const cardTop = wide
    ? (stage.h - cardH) / 2
    : Math.max(band[0] - 60, (band[0] + band[1] - group) / 2);
  const chat: React.CSSProperties = wide
    ? {
        left: stage.safe.left + cardW + 50,
        right: stage.safe.right - 20,
        top: 0,
        bottom: 0,
        justifyContent: "center",
      }
    : {
        left: stage.safe.left,
        // The reply bubble (the brand's link) is right-aligned: clear of
        // the Reels/TikTok action rail.
        right: stage.rail,
        top: cardTop + cardH - font * 1.3,
      };

  return (
    <AbsoluteFill>
      <Backdrop src={img.src} />
      <PhotoCard
        img={img}
        x={wide ? stage.safe.left + cardW / 2 : stage.w / 2}
        y={cardTop + cardH / 2}
        width={cardW}
        height={cardH}
        at={0}
        rotate={-1.5}
        from={[0, 0]}
        zoom={[1.0, 1.12]}
      />
      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          gap: font * 0.45,
          ...chat,
        }}
      >
        <Bubble side="in" at={ASK} fontSize={font} maxWidth={wide ? 700 : 760}>
          {copy.hook.ask}
        </Bubble>
        <Bubble
          side="out"
          at={REPLY}
          fontSize={font}
          maxWidth={wide ? 720 : 780}
        >
          {t(copy.hook.reply)}
          <LinkPreview m={m} fontSize={font} />
        </Bubble>
      </div>
      <Sfx sound="bubble-in" at={ASK} />
      <Sfx sound="bubble-out" at={REPLY} />
    </AbsoluteFill>
  );
};
